import { createSelector } from '@ngrx/store';
import { BookingParkingReservation, BookingTariff } from '@products/models/booking.model';
import { getAllBookedTariffs, getBookedTariff } from '@store/products/booking/product/product-booking.selectors';

/**
 * Get all booked parking tariffs
 * @description Get all booked parking tariffs with parking reservation
 * @return Booked tariff or empty array
 */
export const getAllBookedParkingTariffs = createSelector(getAllBookedTariffs, (bookedTariffs: BookingTariff[]) => {
  const parkingBookedTariffs: BookingTariff[] = [];

  bookedTariffs.forEach((bookedTariff) => {
    if (bookedTariff.parkingReservations.length) {
      parkingBookedTariffs.push(bookedTariff);
    }
  });

  return parkingBookedTariffs;
});

/**
 * Get all booked parking reservations
 * @description Get all booked parking reservations
 * @return Parking reservation or empty array
 */
export const getAllBookedParkingReservations = createSelector(
  getAllBookedParkingTariffs,
  (bookedTariffs: BookingTariff[]) => {
    const bookedParkingReservations: BookingParkingReservation[] = [];

    bookedTariffs.forEach((bookedTariff) =>
      bookedTariff.parkingReservations.forEach((bookedParkingReservation) =>
        bookedParkingReservations.push(bookedParkingReservation)
      )
    );

    return bookedParkingReservations;
  }
);

/**
 * Are all booked parking reservations valid
 * @description Check if all booked parking reservations are valid
 * @returns Boolean
 */
export const areBookedParkingReservationsValid = createSelector(
  getAllBookedParkingReservations,
  (bookedParkingReservations: BookingParkingReservation[]) =>
    bookedParkingReservations.every((bookedParkingReservation) => bookedParkingReservation.isValid)
);

/**
 * Get booked tariff parking reservations
 * @description Get booked tariff parking reservations array
 * @param ticketTypeId
 * @param ticketPersonId
 * @param voucherCode
 * @param packageNumber
 * @param packageIndex
 * @returns Parking reservation or empty array
 */
export const getBookedTariffParkingReservations = (
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode?: string,
  packageNumber?: number,
  packageIndex?: number
) =>
  createSelector(
    getBookedTariff(ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex),
    (bookedTariff: BookingTariff): BookingParkingReservation[] => (bookedTariff ? bookedTariff.parkingReservations : [])
  );

/**
 * Are booked tariff parking reservations valid
 * @description Check if booked tariff parking reservations are valid
 * @param ticketTypeId
 * @param ticketPersonId
 * @param voucherCode
 * @param packageNumber
 * @param packageIndex
 * @returns Boolean
 */
export const areBookedTariffParkingReservationsValid = (
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode?: string,
  packageNumber?: number,
  packageIndex?: number
) =>
  createSelector(
    getBookedTariffParkingReservations(ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex),
    (bookedParkingReservations: BookingParkingReservation[]) =>
      bookedParkingReservations.every((bookedParkingReservation) => bookedParkingReservation.isValid)
  );

/**
 * Get booked tariff parking reservations with inserted license plate number
 * @description Get booked tariff parking reservations array with inserted license plate number
 * @param ticketTypeId
 * @param ticketPersonId
 * @param voucherCode
 * @param packageNumber
 * @param packageIndex
 * @returns Parking reservation or empty array
 */
export const getBookedTariffParkingReservationsWithLicensePlateNumber = (
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode?: string,
  packageNumber?: number,
  packageIndex?: number
) =>
  createSelector(
    getBookedTariffParkingReservations(ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex),
    (bookedParkingReservations: BookingParkingReservation[]): BookingParkingReservation[] =>
      bookedParkingReservations.filter((bookedParkingReservation) => bookedParkingReservation.licensePlateNumber.length)
  );
